import { deDE } from "@mui/material/locale"
import {
  createTheme,
  responsiveFontSizes,
  ThemeOptions,
} from "@mui/material/styles"
import { colors } from "styles/colors"

// Force dark mode regardless of browser settings
// const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
const prefersDark = true

const baseTypography: ThemeOptions = {
  typography: {
    fontFamily: '"Overpass", sans-serif',

    h3: {
      fontSize: "1.2rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },

    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1,
    },
  },
}

// Component styles for Dark Theme
const darkThemeComponentStyles: ThemeOptions["components"] = {
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: "Overpass, sans-serif",
        backgroundColor: colors.background.dark,
        padding: "1rem 2rem",
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: "1rem 2rem 0rem 2rem !important",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        background: colors.background.dark,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        background: colors.background.dark,
        padding: "2rem 2rem",
        gap: "0.5rem",
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          minHeight: "50px",
          borderRadius: "0px 0px 5px 5px",
          borderTop: `2px solid ${colors.border.top.default}`,
          background: colors.background.input,
          boxShadow: `0px 4px 4px 0px ${colors.shadow.input} inset`,
          "&:hover": {
            borderTop: `2px solid ${colors.border.top.hover}`,
          },
          "&.Mui-focused": {
            borderTop: `2px solid ${colors.border.top.focused}`,
          },
          "& fieldset": {
            border: "none",
          },
          "& input": {
            padding: "0 15px",
          },
          "&.Mui-disabled": {
            background: "rgba(185, 192, 200, 0.25)",
            borderTop: `2px solid rgb(188, 188, 188)`,
            boxShadow: "none",
            "& input": {
              color: "rgb(216, 216, 216)",
              WebkitTextFillColor: "rgb(205, 205, 205)",
            },
          },
        },
        "& .MuiInputLabel-root": {
          fontSize: "1rem",
          color: colors.text.label.default,
          "&.Mui-focused": {
            color: colors.text.label.focused,
          },
          "&.Mui-disabled": {
            color: "rgb(216, 216, 216)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
            padding: "0 8px",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              height: "4px",
              background: colors.background.dark,
              zIndex: -1,
            },
          },
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          height: "50px",
          borderRadius: "0px 0px 5px 5px",
          borderTop: `2px solid ${colors.border.top.default}`,
          background: colors.background.input,
          boxShadow: `0px 4px 4px 0px ${colors.shadow.input} inset`,
          "&:hover": {
            borderTop: `2px solid ${colors.border.top.hover}`,
          },
          "&.Mui-focused": {
            borderTop: `2px solid ${colors.border.top.focused}`,
          },
          "& fieldset": {
            border: "none",
          },
          "& input": {
            padding: "0px 15px",
          },
        },
        "& .MuiInputLabel-root": {
          fontSize: "1rem",
          color: colors.text.label.default,
          "&.Mui-focused": {
            color: colors.text.label.focused,
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
            padding: "0 8px",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              height: "4px",
              background: colors.background.dark,
              zIndex: -1,
            },
          },
        },
      },
      popper: {
        "& .MuiPaper-root": {
          backgroundColor: colors.background.dark,
          borderRadius: "5px",
          marginTop: "4px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
        },
        "& .MuiAutocomplete-listbox": {
          padding: "8px",
          "& .MuiAutocomplete-option": {
            padding: "8px 16px",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            "&[aria-selected='true']": {
              backgroundColor: "rgba(255, 255, 255, 0.15)",
            },
          },
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        height: "3rem",
        px: "3rem",
        fontSize: "1rem",
        fontWeight: "600",
        fontFamily: "Overpass, sans-serif",
        letterSpacing: "0.5px",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
        textTransform: "none",
        whiteSpace: "nowrap",
        "& .MuiButton-startIcon": {
          marginBottom: "3px",
        },
        // Toolpad authentication button specific styles
        "&[data-toolpad-color='inherit']": {
          minWidth: "120px",
          whiteSpace: "nowrap",
          padding: "8px 16px",
        },
        // Additional selectors for Toolpad buttons
        "&.ToolpadCore-signInButton, &.ToolpadCore-accountPreview": {
          minWidth: "120px",
          whiteSpace: "nowrap",
          padding: "8px 16px",
        },
        // Target buttons in Toolpad header/toolbar
        ".ToolpadCore-appBarToolbar &": {
          minWidth: "120px",
          whiteSpace: "nowrap",
          padding: "8px 16px",
        },
        "&.cancel-button": {
          backgroundColor: colors.button.cancel.background,
          color: colors.button.cancel.text,
          "&:hover": {
            backgroundColor: colors.button.cancel.hover,
          },
          "&:focus": {
            backgroundColor: colors.button.cancel.focused,
          },
        },
        "&.save-button": {
          backgroundColor: colors.button.save.background,
          color: colors.button.save.text,
          "&:hover": {
            backgroundColor: colors.button.save.hover,
          },
          "&:focus": {
            backgroundColor: colors.button.save.focused,
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState }: { ownerState: any }) => ({
        borderRadius: "5px",
        height: "32px",
        "& .MuiChip-label": {
          padding: "0 12px",
          fontSize: "1rem",
        },
        "& .MuiChip-deleteIcon": {
          width: "25px",
          height: "25px",
          margin: "0 10px 0 0",
        },
        "& .MuiAvatar-root": {
          width: "20px",
          height: "20px",
          marginLeft: "10px",
        },
        // Only apply grey styling when no color prop is provided
        ...(ownerState.color === "default" && {
          backgroundColor: "#3e3c3c",
          color: "white",
          "& .MuiChip-deleteIcon": {
            color: "white",
            "&:hover": {
              color: "#CCCCCC",
            },
          },
        }),
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: colors.background.dark,
      },
    },
  },
}

// Component styles for Light Theme
const lightThemeComponentStyles: ThemeOptions["components"] = {
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: "Overpass, sans-serif",
        backgroundColor: colors.background.light,
        padding: "1rem 2rem",
        color: "#333333",
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: "1rem 2rem 0rem 2rem !important",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        background: colors.background.light,
        color: "#333333",
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        background: colors.background.light,
        padding: "2rem 2rem",
        gap: "0.5rem",
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          minHeight: "50px",
          borderRadius: "0px 0px 5px 5px",
          borderTop: `2px solid #BDBDBD`,
          background: "#F5F5F5",
          color: "#212121",
          boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.05)",
          "&:hover": {
            borderTop: `2px solid #757575`,
            background: "#F9F9F9",
          },
          "&.Mui-focused": {
            borderTop: `2px solid ${colors.border.top.focused || "#2962FF"}`,
            background: "#FFFFFF",
            boxShadow: `0px 0px 0px 2px ${(colors.border.top.focused || "#2962FF").replace(")", ", 0.2)").replace("rgb", "rgba")}`,
          },
          "& fieldset": {
            border: "none",
          },
          "& input": {
            padding: "0 15px",
            color: "#212121",
            WebkitTextFillColor: "#212121",
          },
          "&.Mui-disabled": {
            background: "#F5F5F5",
            borderTop: `2px solid #E0E0E0`,
            boxShadow: "none",
            "& input": {
              color: "#9E9E9E",
              WebkitTextFillColor: "#9E9E9E",
            },
          },
        },
        "& .MuiInputLabel-root": {
          fontSize: "1rem",
          color: "#616161",
          "&.Mui-focused": {
            color: colors.text.label.focused || "#2962FF",
          },
          "&.Mui-disabled": {
            color: "#BDBDBD",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
            padding: "0 8px",
            color: "#424242",
            "&.Mui-focused": {
              color: "#1976D2",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              height: "4px",
              background: colors.background.light,
              zIndex: -1,
            },
          },
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          height: "50px",
          borderRadius: "0px 0px 5px 5px",
          borderTop: `2px solid #BDBDBD`,
          background: "#F5F5F5",
          color: "#212121",
          boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.05)",
          "&:hover": {
            borderTop: `2px solid #757575`,
            background: "#F9F9F9",
          },
          "&.Mui-focused": {
            borderTop: `2px solid ${colors.border.top.focused || "#2962FF"}`,
            background: "#FFFFFF",
            boxShadow: `0px 0px 0px 2px ${(colors.border.top.focused || "#2962FF").replace(")", ", 0.2)").replace("rgb", "rgba")}`,
          },
          "& fieldset": {
            border: "none",
          },
          "& input": {
            padding: "0px 15px",
            color: "#212121",
            WebkitTextFillColor: "#212121",
          },
        },
        "& .MuiInputLabel-root": {
          fontSize: "1rem",
          color: "#616161",
          "&.Mui-focused": {
            color: colors.text.label.focused || "#2962FF",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
            padding: "0 8px",
            color: "#424242",
            "&.Mui-focused": {
              color: "#1976D2",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              height: "4px",
              background: colors.background.light,
              zIndex: -1,
            },
          },
        },
      },
      popper: {
        "& .MuiPaper-root": {
          backgroundColor: colors.background.light,
          borderRadius: "5px",
          marginTop: "4px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        },
        "& .MuiAutocomplete-listbox": {
          padding: "8px",
          "& .MuiAutocomplete-option": {
            padding: "8px 16px",
            borderRadius: "4px",
            color: "#212121",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "&[aria-selected='true']": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              fontWeight: 600,
            },
          },
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        height: "3rem",
        px: "3rem",
        fontSize: "1rem",
        fontWeight: "600",
        fontFamily: "Overpass, sans-serif",
        letterSpacing: "0.5px",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        textTransform: "none",
        whiteSpace: "nowrap",
        "& .MuiButton-startIcon": {
          marginBottom: "3px",
        },
        // Toolpad authentication button specific styles
        "&[data-toolpad-color='inherit']": {
          minWidth: "120px",
          whiteSpace: "nowrap",
          padding: "8px 16px",
        },
        // Additional selectors for Toolpad buttons
        "&.ToolpadCore-signInButton, &.ToolpadCore-accountPreview": {
          minWidth: "120px",
          whiteSpace: "nowrap",
          padding: "8px 16px",
        },
        // Target buttons in Toolpad header/toolbar
        ".ToolpadCore-appBarToolbar &": {
          minWidth: "120px",
          whiteSpace: "nowrap",
          padding: "8px 16px",
        },
        "&.cancel-button": {
          backgroundColor: colors.button.cancel.background || "#F5F5F5",
          color: colors.button.cancel.text || "#546E7A",
          border: `1px solid ${colors.button.cancel.background || "#CFD8DC"}`,
          "&:hover": {
            backgroundColor: colors.button.cancel.hover || "#EEEEEE",
          },
          "&:focus": {
            backgroundColor: colors.button.cancel.focused || "#E0E0E0",
          },
        },
        "&.save-button": {
          backgroundColor: colors.button.save.background || "#2962FF",
          color: colors.button.save.text || "#FFFFFF",
          "&:hover": {
            backgroundColor: colors.button.save.hover || "#1E88E5",
          },
          "&:focus": {
            backgroundColor: colors.button.save.focused || "#0D47A1",
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState }: { ownerState: any }) => ({
        borderRadius: "16px",
        height: "32px",
        fontWeight: 500,
        "& .MuiChip-label": {
          padding: "0 12px",
        },
        "& .MuiChip-deleteIcon": {
          width: "20px",
          height: "20px",
          margin: "0 4px 0 -4px",
        },
        "& .MuiAvatar-root": {
          width: "24px",
          height: "24px",
          marginLeft: "5px",
          color: "#FFFFFF",
          backgroundColor: "#1976D2",
        },
        // Only apply grey styling when no color prop is provided
        ...(ownerState.color === "default" && {
          backgroundColor: "#3e3c3c",
          color: "white",
          "& .MuiChip-deleteIcon": {
            color: "white",
            "&:hover": {
              color: "#CCCCCC",
            },
          },
        }),
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: colors.background.light,
      },
    },
  },
}

let darkTheme = createTheme(
  {
    palette: {
      mode: "dark",
    },
    components: darkThemeComponentStyles,
  },
  baseTypography,
  deDE
)

let lightTheme = createTheme(
  {
    palette: {
      mode: "light",
    },
    components: lightThemeComponentStyles,
  },
  baseTypography,
  deDE
)

lightTheme = responsiveFontSizes(lightTheme)
darkTheme = responsiveFontSizes(darkTheme)

export const baseTheme = prefersDark ? darkTheme : lightTheme
